import React, { useState } from 'react';
import { Gift, Snowflake, Heart, Home, Users, Star, Clock, TreePine } from 'lucide-react';

const ChristmasPage = ({ setCurrentPage }) => {
  const [selectedCategory, setSelectedCategory] = useState('all');

  const christmasCategories = [
    { id: 'cozy', name: 'Koselige gaver', icon: Heart, color: 'red' },
    { id: 'family', name: 'Hele familien', icon: Users, color: 'green' },
    { id: 'tradition', name: 'Juletradisjoner', icon: TreePine, color: 'blue' },
    { id: 'homemade', name: 'Hjemmelaget', icon: Home, color: 'purple' }
  ];

  return (
    <div className="min-h-screen bg-secondary-50">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-error-50 via-success-50 to-error-50 py-16 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 text-4xl">❄️</div>
          <div className="absolute top-20 right-20 text-3xl">🎄</div>
          <div className="absolute bottom-20 left-20 text-5xl">⭐</div>
          <div className="absolute bottom-10 right-10 text-4xl">🎁</div>
        </div>
        <div className="container mx-auto px-4 relative">
          <div className="max-w-4xl mx-auto text-center">
            <div className="text-8xl mb-6">🎄</div>
            <h1 className="text-5xl font-bold text-secondary-800 mb-4">Julegaver</h1>
            <p className="text-xl text-secondary-600 max-w-2xl mx-auto mb-8">
              Spred julens glede og varme med gaver som skaper magiske øyeblikk og kjære minner. 
              Fra koselige kveldsopplevelser til tradisjoner som forenes generasjoner.
            </p>
            <button
              onClick={() => setCurrentPage('gift-finder')}
              className="px-8 py-4 bg-gradient-to-r from-primary-500 to-purple-600 text-white rounded-lg hover:from-primary-600 hover:to-purple-700 transition-all transform hover:scale-105 shadow-lg font-semibold"
            >
              <Gift className="w-5 h-5 inline mr-2" />
              Finn julegaver
            </button>
          </div>
        </div>
      </section>

      {/* Christmas Spirit */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-center text-secondary-800 mb-12">
              Hva gjør en julegave spesiell?
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
              <div className="bg-gradient-to-br from-error-50 to-primary-50 rounded-lg p-6 border border-error-100">
                <Heart className="w-12 h-12 text-error-500 mb-4" />
                <h3 className="text-xl font-semibold text-secondary-800 mb-3">Varme og kjærlighet</h3>
                <p className="text-secondary-600">
                  Julegaver handler om å vise omsorg og kjærlighet. Det er ikke prisen som teller, 
                  men tanken og tiden du har lagt ned i å finne noe som viser hvor mye personen betyr for deg.
                </p>
              </div>

              <div className="bg-gradient-to-br from-success-50 to-emerald-50 rounded-lg p-6 border border-success-100">
                <Users className="w-12 h-12 text-success-500 mb-4" />
                <h3 className="text-xl font-semibold text-secondary-800 mb-3">Samhold og tradisjon</h3>
                <p className="text-secondary-600">
                  Gode julegaver forener familien og skaper tradisjoner som kan gå i arv. 
                  De beste gavene er ofte de som kan deles eller som bidrar til felles opplevelser.
                </p>
              </div>

              <div className="bg-gradient-to-br from-accent-50 to-indigo-50 rounded-lg p-6 border border-accent-100">
                <Snowflake className="w-12 h-12 text-accent-500 mb-4" />
                <h3 className="text-xl font-semibold text-secondary-800 mb-3">Magiske øyeblikk</h3>
                <p className="text-secondary-600">
                  Jul handler om å skape magi og undring. Gaver som vekker glede, overraskelse 
                  eller nostalgiske følelser bidrar til julens spesielle atmosfære.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Christmas Categories */}
      <section className="py-16 bg-secondary-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-center text-secondary-800 mb-12">
              Utforsk julegave-kategorier
            </h2>

            {/* Category Filter */}
            <div className="flex flex-wrap justify-center gap-3 mb-12">
              <button
                onClick={() => setSelectedCategory('all')}
                className={`px-4 py-2 rounded-lg border transition-colors ${
                  selectedCategory === 'all'
                    ? 'bg-error-500 text-white border-error-500'
                    : 'bg-white text-secondary-700 border-secondary-300 hover:border-error-300'
                }`}
              >
                Alle kategorier
              </button>
              {christmasCategories.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`px-4 py-2 rounded-lg border transition-colors flex items-center space-x-2 ${
                    selectedCategory === category.id
                      ? `bg-${category.color}-500 text-white border-${category.color}-500`
                      : 'bg-white text-secondary-700 border-secondary-300 hover:border-secondary-400'
                  }`}
                >
                  <category.icon className="w-4 h-4" />
                  <span>{category.name}</span>
                </button>
              ))}
            </div>

            {/* Category Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Cozy Gifts */}
              <div className={`bg-white rounded-lg shadow-sm p-8 border border-secondary-100 ${
                selectedCategory === 'all' || selectedCategory === 'cozy' ? 'block' : 'hidden'
              }`}>
                <div className="flex items-center mb-6">
                  <Heart className="w-8 h-8 text-error-500 mr-3" />
                  <h3 className="text-2xl font-semibold text-secondary-800">Koselige gaver</h3>
                </div>
                <p className="text-secondary-600 mb-6">
                  Gaver som skaper varme og komfort i de kalde vintermånedene
                </p>
                <div className="space-y-4">
                  <div className="border-l-4 border-error-200 pl-4">
                    <h4 className="font-semibold text-secondary-800">🧶 Varme tekstiler</h4>
                    <ul className="text-sm text-secondary-600 mt-1 space-y-1">
                      <li>• Cashmere-skjerf og luer</li>
                      <li>• Ullsokker og tøfler</li>
                      <li>• Pledd og tepper</li>
                      <li>• Flanell-pyjamas</li>
                    </ul>
                  </div>
                  <div className="border-l-4 border-error-200 pl-4">
                    <h4 className="font-semibold text-secondary-800">☕ Varme drikker og stemning</h4>
                    <ul className="text-sm text-secondary-600 mt-1 space-y-1">
                      <li>• Spesialkaffe og te-kolleksjoner</li>
                      <li>• Vakre krus og termoskopper</li>
                      <li>• Duftlys og essential oils</li>
                      <li>• Kakao og marshmallows</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Family Gifts */}
              <div className={`bg-white rounded-lg shadow-sm p-8 border border-secondary-100 ${
                selectedCategory === 'all' || selectedCategory === 'family' ? 'block' : 'hidden'
              }`}>
                <div className="flex items-center mb-6">
                  <Users className="w-8 h-8 text-success-500 mr-3" />
                  <h3 className="text-2xl font-semibold text-secondary-800">Hele familien</h3>
                </div>
                <p className="text-secondary-600 mb-6">
                  Gaver som samler generasjonene og skaper felles opplevelser
                </p>
                <div className="space-y-4">
                  <div className="border-l-4 border-success-200 pl-4">
                    <h4 className="font-semibold text-secondary-800">🎮 Aktiviteter og spill</h4>
                    <ul className="text-sm text-secondary-600 mt-1 space-y-1">
                      <li>• Brettspill for alle aldre</li>
                      <li>• Puslespill med jule-motiver</li>
                      <li>• Karaoke-sett for hjemmet</li>
                      <li>• Utendørs vinter-aktiviteter</li>
                    </ul>
                  </div>
                  <div className="border-l-4 border-success-200 pl-4">
                    <h4 className="font-semibold text-secondary-800">🍪 Kjøkken og baking</h4>
                    <ul className="text-sm text-secondary-600 mt-1 space-y-1">
                      <li>• Pepperkake-hus sett</li>
                      <li>• Julekake-former og redskaper</li>
                      <li>• Famile-kokebok</li>
                      <li>• Sjokolade-fondue sett</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Traditional Gifts */}
              <div className={`bg-white rounded-lg shadow-sm p-8 border border-secondary-100 ${
                selectedCategory === 'all' || selectedCategory === 'tradition' ? 'block' : 'hidden'
              }`}>
                <div className="flex items-center mb-6">
                  <TreePine className="w-8 h-8 text-accent-500 mr-3" />
                  <h3 className="text-2xl font-semibold text-secondary-800">Juletradisjoner</h3>
                </div>
                <p className="text-secondary-600 mb-6">
                  Gaver som hedrer norske juletradisjoner og skaper nostalgi
                </p>
                <div className="space-y-4">
                  <div className="border-l-4 border-accent-200 pl-4">
                    <h4 className="font-semibold text-secondary-800">🎄 Julepynt og dekorasjoner</h4>
                    <ul className="text-sm text-secondary-600 mt-1 space-y-1">
                      <li>• Håndlagde julekugler</li>
                      <li>• Nisser og jule-figurer</li>
                      <li>• Adventslys og kranser</li>
                      <li>• Vintage julepynt</li>
                    </ul>
                  </div>
                  <div className="border-l-4 border-accent-200 pl-4">
                    <h4 className="font-semibold text-secondary-800">📖 Jule-kulturen</h4>
                    <ul className="text-sm text-secondary-600 mt-1 space-y-1">
                      <li>• Klassiske julebøker</li>
                      <li>• Julemusikk samlinger</li>
                      <li>• Julekalendere (for voksne også!)</li>
                      <li>• Norske juletradisjoner-bøker</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Homemade Gifts */}
              <div className={`bg-white rounded-lg shadow-sm p-8 border border-secondary-100 ${
                selectedCategory === 'all' || selectedCategory === 'homemade' ? 'block' : 'hidden'
              }`}>
                <div className="flex items-center mb-6">
                  <Home className="w-8 h-8 text-purple-500 mr-3" />
                  <h3 className="text-2xl font-semibold text-secondary-800">Hjemmelaget</h3>
                </div>
                <p className="text-secondary-600 mb-6">
                  Personlige gaver laget med kjærlighet og omtanke
                </p>
                <div className="space-y-4">
                  <div className="border-l-4 border-purple-200 pl-4">
                    <h4 className="font-semibold text-secondary-800">🍯 Mat og drikke</h4>
                    <ul className="text-sm text-secondary-600 mt-1 space-y-1">
                      <li>• Hjemmelaget konfekt og trøfler</li>
                      <li>• Syltetøy og marmelade</li>
                      <li>• Krydder-blandinger</li>
                      <li>• Infusert olje og eddik</li>
                    </ul>
                  </div>
                  <div className="border-l-4 border-purple-200 pl-4">
                    <h4 className="font-semibold text-secondary-800">✂️ Håndverk og sying</h4>
                    <ul className="text-sm text-secondary-600 mt-1 space-y-1">
                      <li>• Strikkede plagg og tilbehør</li>
                      <li>• Broderte håndklær</li>
                      <li>• Hjemmelagde såper</li>
                      <li>• Personaliserte fotobøker</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Christmas Timeline */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-center text-secondary-800 mb-12">
              Jule-planlegging: Fra november til nyttår
            </h2>

            <div className="space-y-8">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-accent-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-accent-600 font-bold">NOV</span>
                </div>
                <div className="bg-accent-50 rounded-lg p-6 flex-1 border border-accent-200">
                  <h3 className="text-lg font-semibold text-secondary-800 mb-2">November: Planlegging og early bird</h3>
                  <p className="text-secondary-600 mb-3">
                    Perfekt tid for de som liker å være tidlig ute. Butikkene har ofte pre-christmas salg.
                  </p>
                  <ul className="text-sm text-secondary-600 space-y-1">
                    <li>• Lag gavelister for alle familiemedlemmer</li>
                    <li>• Kjøp online med god leveringstid</li>
                    <li>• Start hjemmelagde gaver som trenger tid</li>
                    <li>• Black Friday tilbud (slutten av november)</li>
                  </ul>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-success-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-success-600 font-bold">DES</span>
                </div>
                <div className="bg-success-50 rounded-lg p-6 flex-1 border border-success-200">
                  <h3 className="text-lg font-semibold text-secondary-800 mb-2">Desember: Høysesong og julehandel</h3>
                  <p className="text-secondary-600 mb-3">
                    Mest populære tiden for julegaver. Forvent køer og at populære varer kan være utsolgt.
                  </p>
                  <ul className="text-sm text-secondary-600 space-y-1">
                    <li>• 1-15. des: Optimal tid for de fleste gaver</li>
                    <li>• 16-20. des: Last minute shopping, begrenset utvalg</li>
                    <li>• 21-23. des: Kun gavekort og digitale gaver</li>
                    <li>• Julemarkeder og pop-up butikker</li>
                  </ul>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-purple-600 font-bold">JAN</span>
                </div>
                <div className="bg-purple-50 rounded-lg p-6 flex-1 border border-purple-200">
                  <h3 className="text-lg font-semibold text-secondary-800 mb-2">Januar: Etter-jul og planlegging</h3>
                  <p className="text-secondary-600 mb-3">
                    Tid for å utveksle gaver som ikke passet, og planlegge for neste år.
                  </p>
                  <ul className="text-sm text-secondary-600 space-y-1">
                    <li>• Januarsalg - kjøp til neste år</li>
                    <li>• Byt/returner gaver som ikke passet</li>
                    <li>• Noter deg hva som var populært</li>
                    <li>• Start sparekonto for neste års julegaver</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Budget and Stress Management */}
      <section className="py-16 bg-secondary-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-3xl font-bold text-center text-secondary-800 mb-12">
              Håndter julebudsjett og stress
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-white rounded-lg shadow-sm p-6 border border-secondary-100">
                <div className="flex items-center mb-4">
                  <Star className="w-8 h-8 text-warning-500 mr-3" />
                  <h3 className="text-xl font-semibold text-secondary-800">Smart budsjett-tips</h3>
                </div>
                <ul className="space-y-3 text-secondary-600">
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-warning-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    <span>Sett en øvre grense for totalt julebudsjett tidlig</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-warning-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    <span>Fordel budsjettet per person på lista di</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-warning-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    <span>Husk å inkludere mat, pynt og aktiviteter</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-warning-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    <span>Vurder fellesgaver til dyre ting</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-warning-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    <span>Opplevelser kan være rimeligere enn fysiske gaver</span>
                  </li>
                </ul>
              </div>

              <div className="bg-white rounded-lg shadow-sm p-6 border border-secondary-100">
                <div className="flex items-center mb-4">
                  <Heart className="w-8 h-8 text-error-500 mr-3" />
                  <h3 className="text-xl font-semibold text-secondary-800">Reduser julestress</h3>
                </div>
                <ul className="space-y-3 text-secondary-600">
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-error-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    <span>Husk: En ok gave i tide er bedre enn den perfekte for sent</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-error-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    <span>Del gjøremål med familie - ikke ta alt alene</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-error-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    <span>Lag lister og sjekk av - få kontroll på kaoset</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-error-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    <span>Gi deg selv lov til å si nei til noen tilbud</span>
                  </li>
                  <li className="flex items-start">
                    <span className="w-2 h-2 bg-error-500 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                    <span>Fokus på opplevelser og samvær, ikke perfeksjon</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="mt-8 bg-error-50 border border-error-200 rounded-lg p-6">
              <div className="flex items-start space-x-4">
                <Clock className="w-6 h-6 text-error-600 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="text-lg font-semibold text-error-800 mb-2">Viktig påminnelse</h3>
                  <p className="text-error-700">
                    Jul handler ikke om hvor mye penger du bruker eller hvor mange gaver du gir. 
                    Det handler om kjærlighet, samvær og det å vise at du bryr deg. 
                    En hjertelig hilsen og et varmt klem kan være den beste gaven av alle.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-error-500 to-success-600">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-white mb-4">
              Klar til å spre julens glede?
            </h2>
            <p className="text-xl text-error-100 mb-8">
              Bruk vår gavefinner for å finne gaver som skaper varme og magiske juleøyeblikk
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => setCurrentPage('gift-finder')}
                className="px-8 py-4 bg-white text-error-600 rounded-lg hover:bg-secondary-100 transition-colors font-semibold shadow-lg"
              >
                <Gift className="w-5 h-5 inline mr-2" />
                Start gavefinneren
              </button>
              <button
                onClick={() => setCurrentPage('occasion')}
                className="px-8 py-4 border-2 border-white text-white rounded-lg hover:bg-white hover:text-error-600 transition-colors font-semibold"
              >
                Se andre anledninger
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ChristmasPage;